import moment from 'moment-timezone';
import config from '../../config';
import { CREATE, GET_LIST, GET_ONE, UPDATE, UPDATE_BATCH, DELETE } from '../../api/dataProvider';
import {
  SAMPLE_ITEMS, SAMPLE_ANSWERS, SAMPLE_LONG_TAIL, SAMPLE_PRODUCTS, SAMPLE_TAGS, SAMPLE_ACCESS_TAGS,
} from './sampleData';
import { teamDiffers } from './teamAnswers';
import { CHANNEL_COPY } from './channelCopy';

/*
 * Knowledge Review data access. SAMPLE DATA BY DEFAULT, in every environment including
 * production: the screens are being iterated with clients before any operator goes live
 * (Jordan, 2026-09-24). An operator switches to the real backend with the
 * `knowledge_review_live` config flag -- no code change. (Seeing the tab at all in
 * production is the separate `knowledge_review_enabled` flag.) Both modes return the same
 * shapes -- see the API contract in
 * cplanetbackend/aidocs/plans/2026-09-24-knowledge-review-inbox.md.
 *
 * Every call is scoped to one channel: the AI Suite inbox is `support`, the Web Chat
 * Widget inbox is `widget`. A trained answer can be SHARED (one answer, `channels` lists
 * both) or COPIED (an independent answer in the other channel).
 */
// `knowledge_review_enabled` = "staff": only these accounts see the inbox, with the real (mined)
// cards -- reviewing them before the operator's own staff do. Mirrors the backend's
// KNOWLEDGE_REVIEW_STAFF_EMAILS (demo.lib.knowledge_review.inbox_open_to), which refuses
// everyone else.
export const KNOWLEDGE_REVIEW_STAFF_EMAILS = ['help@curatedplanet.com'];
const STAFF_ONLY = 'staff';

const bootstrapUserEmail = () => {
  try {
    const email = JSON.parse(window.localStorage.getItem('bootstrap') || '{}')?.user?.email;
    return typeof email === 'string' ? email.trim().toLowerCase() : null;
  } catch {
    return null;
  }
};

const isStaffPreview = (tourOperator) => tourOperator?.config?.knowledge_review_enabled === STAFF_ONLY
  && KNOWLEDGE_REVIEW_STAFF_EMAILS.includes(bootstrapUserEmail());

// Real data (else sample data). The staff preview is always real: that's what it is for.
export const isLiveFor = (tourOperator) => !!tourOperator?.config?.knowledge_review_live
  || isStaffPreview(tourOperator);

// The inbox tabs (and their menu badges): always outside production; in production only when
// `knowledge_review_enabled` is true, or "staff" for the staff accounts above. Callers add
// their own page permission check.
export const trainingInboxVisibleFor = (tourOperator) => !config.isProduction
  || tourOperator?.config?.knowledge_review_enabled === true
  || isStaffPreview(tourOperator);

// Whose switches the two checks above read. A franchise switches the inbox on per licensee: each
// licensee in the bootstrap carries its own setting, else the parent's (backend
// build_franchise_payload). Its staff sign in under the parent, so the signed-in operator only
// decides for the parent itself, standalone operators, and a backend that sends no licensee config.
export const inboxOperatorFor = (tourOperator, isFranchise, licensees, licenseeId) => {
  if (!isFranchise || licenseeId == null) return tourOperator;
  const licensee = (licensees || []).find((l) => String(l.id) === String(licenseeId));
  return licensee?.config ? licensee : tourOperator;
};

const ITEMS = 'tour_operator/knowledge_review_items';
const ANSWERS = 'tour_operator/knowledge_answers';

// Outcome labels for the Done view, keyed by item status.
export const OUTCOME_LABELS = {
  approved: 'Trained',
  ignored: 'Ignored',
  kept: 'Kept as is',
};

// Ignore reasons. "It depends on each booking" (per channel wording) replaced a separate
// button with no visible outcome; as a reason it still tells us which booking details
// guests keep asking about.
export const ignoreReasons = (channel) => [
  { value: 'one_off', label: 'It was a one-off question' },
  { value: 'depends_on_booking', label: CHANNEL_COPY[channel].dependsReason },
  { value: 'not_for_bot', label: 'The bot shouldn’t answer this' },
  { value: 'spam', label: 'Spam or a test message' },
];

export const PAGE_SIZE = 25;
// Mock stand-in for the backend's ask_count window.
const MOCK_WINDOW_DAYS = 90;

// A question asked only once is usually noise; the queue hides those unless asked to.
// Only unanswered questions can be one-offs -- conflicts and stale answers always show.
export const isOneOff = (item) => item.kind === 'gap' && item.ask_count < 2;

/*
 * Counts per kind (respecting the one-off switch), how many one-offs are hidden, and the
 * impact line: how few of the top questions account for half of all asks.
 */
const summarize = (items, includeOnce) => {
  const open = items.filter((i) => i.status === 'open');
  const shown = open.filter((i) => includeOnce || !isOneOff(i));
  const asks = shown.map((i) => i.ask_count || 0).filter((n) => n > 0).sort((a, b) => b - a);
  const asksTotal = asks.reduce((a, b) => a + b, 0);
  let running = 0;
  let halfN = 0;
  while (halfN < asks.length && running < asksTotal / 2) running += asks[halfN++];
  return {
    open: shown.length,
    gap: shown.filter((i) => i.kind === 'gap').length,
    conflict: shown.filter((i) => i.kind === 'conflict').length,
    stale: shown.filter((i) => i.kind === 'stale').length,
    team_differs: shown.filter(teamDiffers).length,
    one_off: open.filter(isOneOff).length,
    asks_total: asksTotal,
    asked_questions: asks.length,
    half_of_asks_top_n: halfN,
    window_days: MOCK_WINDOW_DAYS,
  };
};

const matchesSearch = (item, search) => {
  const q = (search || '').trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    item.question,
    ...(item.examples || []).map((e) => e.text),
    ...(item.team_answers || []).map((a) => a.text),
    item.answer?.answer,
  ].filter(Boolean).join(' ').toLowerCase();
  return haystack.includes(q);
};

// The backend's ?tag= / ?atag=: the card's products carry the tag, or its trained answer names it.
const SAMPLE_PRODUCT_TAGS = Object.fromEntries(SAMPLE_PRODUCTS.map((p) => [p.id, { tag: p.tag_ids, atag: p.atag_ids }]));
const hasTag = (item, kind, id) => [...(item.tours || []), ...(item.answer?.tours || [])]
  .some((t) => (SAMPLE_PRODUCT_TAGS[t.id]?.[kind] || []).includes(id))
  || (item.answer?.[kind === 'atag' ? 'access_tags' : 'collections'] || []).some((c) => c.id === id);

const matchesFilter = (item, {
  status, kind, channel, search, tour, tag, atag, includeOnce, teamDiffers: differs,
}) =>
  (!status || (status === 'done' ? item.status !== 'open' : item.status === status))
  && (!differs || teamDiffers(item))
  && (!kind || item.kind === kind)
  && (!channel || item.channel === channel)
  && (status !== 'open' || includeOnce || !isOneOff(item))
  && (!tour || (item.tours || []).some((t) => t.id === tour) || (item.answer?.tours || []).some((t) => t.id === tour))
  && (!tag || hasTag(item, 'tag', tag))
  && (!atag || hasTag(item, 'atag', atag))
  && matchesSearch(item, search);

const byImpact = (a, b) => (b.ask_count - a.ask_count) || moment(b.last_asked_ts).diff(a.last_asked_ts);
const byResolved = (a, b) => moment(b.resolved_ts).diff(a.resolved_ts);

function createMockApi({ longTail = true } = {}) {
  const clone = (x) => JSON.parse(JSON.stringify(x));
  let items = clone(longTail ? [...SAMPLE_ITEMS, ...SAMPLE_LONG_TAIL] : SAMPLE_ITEMS);
  let answers = clone(SAMPLE_ANSWERS);
  let nextAnswerId = 1000;
  const later = (value) => new Promise((resolve) => setTimeout(() => resolve(clone(value)), 120));
  const find = (id) => items.find((i) => i.id === id);
  const findAnswer = (id) => answers.find((a) => a.id === id);
  const stamp = () => ({ approved_by: 'You', approved_ts: moment().toISOString(), last_confirmed_ts: moment().toISOString() });

  // data: what the card sent; unless close_twins is false, the card's open twin in the other
  // inbox closes with it (as the backend), and Undo reopens it.
  const resolve = (id, status, extra = {}, data = {}) => {
    const item = find(id);
    item.status = status;
    item.resolved_ts = moment().toISOString();
    item.resolved_by = 'You';
    Object.assign(item, extra);
    const twins = data.close_twins === false ? [] : (item.twins || []).map((t) => find(t.id)).filter((t) => t && t.status === 'open');
    twins.forEach((t) => Object.assign(t, { status, resolved_ts: item.resolved_ts, resolved_by: 'You', ignore_reason: item.ignore_reason }));
    item.closed_twin_ids = twins.map((t) => t.id);
    return later(item);
  };

  const saveAnswer = (payload, existingId) => {
    const productsById = Object.fromEntries(SAMPLE_PRODUCTS.map(({ id, name }) => [id, { id, name }]));
    const tagsById = Object.fromEntries(SAMPLE_TAGS.map((t) => [t.id, t]));
    const atagsById = Object.fromEntries(SAMPLE_ACCESS_TAGS.map((a) => [a.id, a]));
    const pick = (ids, byId) => (ids || []).map((id) => byId[id]).filter(Boolean);
    // As the backend: a product picked on its own is not also left out.
    const tourIds = new Set(payload.tour_ids || []);
    const existing = existingId && findAnswer(existingId);
    // Who set the stop date: whoever last changed it.
    const dateChanged = (payload.valid_until || null) !== (existing?.valid_until || null);
    const record = {
      question: payload.question,
      answer: payload.answer,
      all_tours: payload.all_tours,
      tours: pick(payload.tour_ids, productsById),
      collections: pick(payload.collection_ids, tagsById),
      access_tags: pick(payload.atag_ids, atagsById),
      excluded_tours: pick((payload.excluded_tour_ids || []).filter((id) => !tourIds.has(id)), productsById),
      channels: payload.channels,
      valid_until: payload.valid_until || null,
      valid_until_reason: payload.valid_until ? (payload.valid_until_reason || null) : null,
      valid_until_set_by: payload.valid_until
        ? (dateChanged ? 'You' : existing?.valid_until_set_by || 'You')
        : null,
      ...stamp(),
    };
    if (existing) {
      Object.assign(existing, record);
      return existing;
    }
    const created = { id: nextAnswerId++, ...record };
    answers = [created, ...answers];
    return created;
  };

  return {
    mode: 'mock',
    summary: (channel, { includeOnce = false } = {}) =>
      later(summarize(items.filter((i) => i.channel === channel), includeOnce)),
    // One page of { results, count }, highest impact first (done: most recent first).
    listItems: ({ page = 1, pageSize = PAGE_SIZE, ...filter }) => {
      const all = items.filter((i) => matchesFilter(i, filter)).sort(filter.status === 'done' ? byResolved : byImpact);
      // Only twins still open, as the backend reports them.
      const withTwins = (i) => ({ ...i, twins: (i.twins || []).filter((t) => find(t.id)?.status === 'open') });
      return later({ results: all.slice((page - 1) * pageSize, page * pageSize).map(withTwins), count: all.length });
    },
    // payload is one answer, or { answers: [...] } for per-product answers to one question.
    approve: (id, payload) => {
      const item = find(id);
      if (payload.answers) {
        const saved = payload.answers.map((p) => saveAnswer(p));
        return resolve(id, 'approved', { saved_answer_ids: saved.map((a) => a.id) }, payload);
      }
      const saved = saveAnswer(payload, item.kind === 'stale' ? item.answer?.id : null);
      return resolve(id, 'approved', {
        saved_answer_ids: [saved.id],
        // A knowledge-base conflict hides its quoted passage from the bot unless opted out.
        passage_hidden: !!(item.kb_excerpt && payload.suppress_kb_excerpt !== false),
      }, payload);
    },
    ignore: (id, data) => resolve(id, 'ignored', { ignore_reason: data?.reason }, data),
    keep: (id, data) => {
      const item = find(id);
      const a = item.kind === 'stale' && item.answer && findAnswer(item.answer.id);
      if (a) {
        a.last_confirmed_ts = moment().toISOString();
        // "Still right" on an expired answer means it no longer expires.
        if (item.stale_reason === 'expired') {
          Object.assign(a, { valid_until: null, valid_until_reason: null, valid_until_set_by: null });
        }
      }
      return resolve(id, 'kept', {}, data);
    },
    removeStaleAnswer: (id, data) => {
      const item = find(id);
      answers = answers.filter((a) => a.id !== item.answer?.id);
      return resolve(id, 'ignored', { ignore_reason: 'removed' }, data);
    },
    reopen: (id) => {
      const item = find(id);
      if (item.saved_answer_ids && item.kind !== 'stale') {
        answers = answers.filter((a) => !item.saved_answer_ids.includes(a.id));
      }
      [item, ...(item.closed_twin_ids || []).map(find)].forEach((i) => {
        i.status = 'open';
        delete i.resolved_ts;
        delete i.resolved_by;
        delete i.ignore_reason;
      });
      delete item.saved_answer_ids;
      delete item.closed_twin_ids;
      return later(item);
    },
    preview: ({ answer }) => later({ reply: answer, sample: true }),
    listAnswers: (channel) => later(answers.filter((a) => a.channels.includes(channel))),
    createAnswer: (payload) => later(saveAnswer(payload)),
    updateAnswer: (id, payload) => later(saveAnswer(payload, id)),
    // Share / stop sharing: the same answer, used in these channels.
    setAnswerChannels: (id, channels) => {
      const a = findAnswer(id);
      a.channels = channels;
      return later(a);
    },
    // Copy: an independent answer in `channel`, edited separately from the original.
    copyAnswer: (id, channel) => {
      const src = findAnswer(id);
      const created = { ...clone(src), id: nextAnswerId++, channels: [channel], copied_from: src.id, ...stamp() };
      answers = [created, ...answers];
      return later(created);
    },
    deleteAnswer: (id) => {
      answers = answers.filter((a) => a.id !== id);
      return later({ id });
    },
    // The products/ response. `featured`: the operator's featured category codes, from its config
    // (the backend reads that config itself; the live call takes no argument).
    listProducts: ({ featured = [] } = {}) => {
      const count = (kind, id) => SAMPLE_PRODUCTS.filter((p) => p[kind].includes(id)).length;
      const option = (t, kind = 'tag_ids') => ({ ...t, product_count: count(kind, t.id) });
      const categories = featured.map((code) => SAMPLE_TAGS.find((t) => t.category.code === code)?.category).filter(Boolean);
      return later({
        count: SAMPLE_PRODUCTS.length,
        results: SAMPLE_PRODUCTS,
        featured: categories.map((c) => ({
          ...c,
          tags: SAMPLE_TAGS.filter((t) => t.category.code === c.code).map(({ id, name }) => option({ id, name })),
        })),
        other_tags: SAMPLE_TAGS.filter((t) => !featured.includes(t.category.code)).map((t) => option(t)),
        access_tags: SAMPLE_ACCESS_TAGS.map((a) => option(a, 'atag_ids')),
      });
    },
  };
}

function createLiveApi(dataAPI, topId, widgetId) {
  const scope = topId ? { tour_operator: topId } : {};
  // CP-8335: on the Web Chat Widget page of an operator with several widgets, the cards and
  // answers of the widget picked there (reads only; actions act on the card itself).
  const readScope = widgetId ? { ...scope, widget: widgetId } : scope;
  const act = (id, action, data = {}) =>
    dataAPI(UPDATE, ITEMS, { id, urlSuffix: `${action}/`, data: { ...data, ...scope } }).then((r) => r.data);
  const list = (resource, filter = {}) =>
    dataAPI(GET_LIST, resource, {
      pagination: { page: 1, perPage: 200 },
      sort: { field: 'id', order: 'DESC' },
      filter: { ...filter, ...readScope },
    }).then((r) => r.data);

  return {
    mode: 'live',
    // Feeds the menu and tab badges, fetched without the user asking: a 403 (no ai.read /
    // web_widget.read) must not sign the session out. The inbox itself still does, via listItems.
    summary: (channel, { includeOnce = false } = {}) => dataAPI(GET_ONE, ITEMS, {
      urlSuffix: 'summary/',
      filter: { channel, min_asks: includeOnce ? 1 : 2, ...scope },
      keepSessionOn403: true,
    }).then((r) => (r.statusCode === 200 ? r.data : null)),
    listItems: ({ page = 1, pageSize = PAGE_SIZE, includeOnce, ...filter }) =>
      dataAPI(GET_LIST, ITEMS, {
        pagination: { page, perPage: pageSize },
        sort: { field: 'id', order: 'DESC' }, // ignored: the backend orders by impact
        filter: {
          ...filter,
          teamDiffers: undefined,
          team_differs: filter.teamDiffers ? true : undefined,
          min_asks: includeOnce ? 1 : 2,
          ...readScope,
        },
      }).then((r) => ({ results: r.data, count: r.total })),
    approve: (id, payload) => act(id, 'approve', payload),
    // data: { reason } for ignore, plus close_twins when the card has a twin in the other inbox.
    ignore: (id, data) => act(id, 'ignore', data),
    keep: (id, data) => act(id, 'keep', data),
    removeStaleAnswer: (id, data) => act(id, 'remove_answer', data),
    reopen: (id) => act(id, 'reopen'),
    preview: (payload) =>
      dataAPI(UPDATE_BATCH, ITEMS, { urlSuffix: 'preview/', data: { ...payload, ...scope } }).then((r) => r.data),
    listAnswers: (channel) => list(ANSWERS, { channel }),
    createAnswer: (payload) => dataAPI(CREATE, ANSWERS, { data: { ...payload, ...scope } }).then((r) => r.data),
    updateAnswer: (id, payload) => dataAPI(UPDATE, ANSWERS, { id, data: payload }).then((r) => r.data),
    setAnswerChannels: (id, channels) =>
      dataAPI(UPDATE, ANSWERS, { id, urlSuffix: 'channels/', data: { channels } }).then((r) => r.data),
    copyAnswer: (id, channel) =>
      dataAPI(UPDATE, ANSWERS, { id, urlSuffix: 'copy_to_channel/', data: { channel } }).then((r) => r.data),
    deleteAnswer: (id) => dataAPI(DELETE, ANSWERS, { id }).then((r) => r.data),
    // Every recurring tour, id and name only (the /tour/ grid took 30-60 s and stopped at 200), with
    // its tag_ids / atag_ids; plus featured / other_tags / access_tags for the pickers (tagScope.js).
    listProducts: () => dataAPI(GET_ONE, ANSWERS, { urlSuffix: 'products/', filter: { ...scope } })
      .then((r) => r.data),
  };
}

// One mock store per page load, shared by both inboxes, so a share made on one page shows
// up on the other and decisions survive tab switches while reviewing.
let mockSingleton = null;
let mockOptions = {};

export function getKnowledgeReviewApi(dataAPI, topId, live = false, widgetId = undefined) {
  if (live) return createLiveApi(dataAPI, topId, widgetId);
  if (!mockSingleton) mockSingleton = createMockApi(mockOptions);
  return mockSingleton;
}

// Test hook: fresh sample data per test. `{ longTail: false }` = the curated set only.
export function resetKnowledgeReviewMock(options = {}) {
  mockSingleton = null;
  mockOptions = options;
}
